/* eslint-disable max-len */
import React, { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Dialog } from 'ui-components/dialog';
import { EmailTags } from 'ui-components/email-tags/email-tags';
import { Button } from 'ui-components/button/button';
import {
    HeaderTextProps,
    MODAL_WIDTH,
    ShareModalSharedProps,
} from 'ui-components/share-report-modal/share-report-modal';
import styles from './share-modal.module.scss';

type ShareModalProps = ShareModalSharedProps & {
    onShareReport: (validEmailsList: string[], messageContent: string) => void;
    onCancelButtonClick: () => void;
    isSendingRequest: boolean;
};

const MESSAGE_MAX_LENGTH = 1024;

export const useGetHeaderTextPropsContent = () => {
    return useCallback(
        (headerContent: HeaderTextProps) => (
            <>
                <div className={styles.primaryText}>{headerContent.primaryText}</div>
                <div className={styles.secondaryText}>{headerContent.secondaryText}</div>
                <div>{headerContent.tertiaryText}</div>
            </>
        ),
        [],
    );
};

export const ShareModal = ({
    headerContent,
    contentBottomElement,
    visible,
    onCloseModal,
    onCancelButtonClick,
    onShareReport,
    isSendingRequest,
}: ShareModalProps) => {
    const [validEmailsList, setValidEmailsList] = useState<string[]>([]);
    const getHeaderTextPropsContent = useGetHeaderTextPropsContent();
    const messageContent = useRef<string>('');
    const onEmailsChanged = (emailList: string[]) => {
        setValidEmailsList(emailList);
    };

    const getHeaderContent = () => {
        if (!React.isValidElement(headerContent)) {
            return getHeaderTextPropsContent(headerContent as HeaderTextProps);
        }
        return headerContent;
    };

    const onMessageContentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        messageContent.current = e.target.value;
    };

    const onSendButtonClick = () => {
        onShareReport(validEmailsList, messageContent.current);
    };

    const onAfterClose = () => {
        messageContent.current = '';
        setValidEmailsList([]);
    };

    return (
        <Dialog
            className={styles.modal}
            visible={visible}
            centered
            destroyOnClose
            closable={false}
            footer={false}
            width={MODAL_WIDTH}
            onCancel={onCloseModal}
            afterClose={onAfterClose}
        >
            <div className={styles.headerContainer}>{getHeaderContent()}</div>
            <hr className={styles.separator} />
            <div className={styles.addEmailContainer}>
                <div className={styles.smallText}>Share via email</div>
                <EmailTags onEmailsChanged={onEmailsChanged} />
                <textarea
                    maxLength={MESSAGE_MAX_LENGTH}
                    placeholder={'Message'}
                    onChange={onMessageContentChange}
                    className={styles.textArea}
                    aria-label={'Add message'}
                />
            </div>
            {contentBottomElement}
            <div className={styles.buttonsContainer}>
                <Button onClick={onCancelButtonClick} className={styles.button}>
                    Cancel
                </Button>
                <Button
                    className={styles.button}
                    type={'brand-primary-full'}
                    disabled={!validEmailsList.length}
                    onClick={onSendButtonClick}
                    loading={isSendingRequest}
                >
                    Send
                </Button>
            </div>
        </Dialog>
    );
};
